import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n } from '@lingui/react'
import compose from 'lodash/fp/compose'
import get from 'lodash/get'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'

import {
  loadImpressions,
  buildUser,
} from 'client/bookmate/reducers/user-reducer'
import { getImpressionsByIds } from 'client/bookmate/reducers/impressions-reducer'

import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserSubHeaderBooksBox from 'client/bookmate/boxes/user-sub-header-books-box'
import HeaderBox from 'client/shared/boxes/header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'
import { CardImpressionBox } from 'client/bookmate/boxes/card-impression-box'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import Spacer from 'client/shared/blocks/spacer'
import { Dispatch, State } from 'shared/types/redux'
import { User } from 'client/shared/types/current-user'
import { ImpressionProps } from 'client/shared/types/impression'

type Props = {
  dispatch: Dispatch
  user: User
  i18n: any
  impressions: ImpressionProps[]
}

class UserImpressionsBox extends Component<Props> {
  load = () => {
    const {
      dispatch,
      user: { login: username },
      impressionsPage,
    } = this.props

    if (username) {
      return dispatch(
        loadImpressions({
          username,
          page: impressionsPage,
          per_page: 20,
          append: true,
        }),
      )
    } else {
      return undefined
    }
  }

  render() {
    const {
      user,
      user: { myself },
      impressions,
      impressionsPage,
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        {myself ? <UserSubHeaderBooksBox /> : <UserInfoBox user={user} />}
        <UserSubHeaderBox hidden={myself} />
        <Spacer size={32} />
        <LayoutColumn>
          <InfiniteScroll load={this.load} page={impressionsPage}>
            {impressions.map((impression, i) => {
              return (
                <CardImpressionBox
                  impression={impression}
                  embedded={impression.resource}
                  key={i}
                />
              )
            })}
          </InfiniteScroll>
          <Spacer size="bottom-page" />
        </LayoutColumn>
      </>
    )
  }

  renderMeta() {
    const {
      user: { name, login },
      impressions,
      i18n,
    } = this.props
    const params = {
      name,
      username: login,
    }
    const title = i18n._('profile.meta_title_impressions', params)
    const common = i18n._('profile.meta_description_impressions', params)
    const content = get(impressions, '0.content')
    const description = common + (content ? `: content` : '')

    return (
      <>
        <MetaTitle title={title} />
        <MetaDescription description={description} />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { username = '' } = params
    const { dispatch } = store

    const load = [dispatch(loadImpressions({ username }))]
    await Promise.all(load)
  },
  {
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  user: buildUser(state, state.user.login),
  impressionsPage: state.user.pages.impressions,
  impressions: getImpressionsByIds(state, state.user.impressions),
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(UserImpressionsBox)
